import { useCallback, useContext, useMemo, useState, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { QuantAnalysisItem } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import { useDimensions } from '@trinity/chat-components/hooks/useDimensions';
import { useSearchFilter } from '@trinity/chat-components/hooks/useQuantSearchFilter';
import { queryKeys } from '@trinity/chat-components/query-keys/index.js';
import {
  SetSelectedQuantAnalysisItemContext,
  QuantAnalysisContext,
  QuantAnalysisVersionContext,
} from '@trinity/chat-components';
import { SearchInput } from '@trinity/chat-components/components/QuantAnalysis/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import { SearchDropdown } from './components';
import styles from './style/QuantAnalysisSearch.module.css';

type Props = {
  options: QuantAnalysisItem[];
};

export const QuantAnalysisSearch = memo(({ options }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);

  const setSelectedItemId = useContext(SetSelectedQuantAnalysisItemContext);
  const quantAnalysis = useContext(QuantAnalysisContext);
  const projectDetails = useContext(ProjectDetailsContext);
  const windowSize = useDimensions();
  const quantVersion = useContext(QuantAnalysisVersionContext);
  const apiClient = useChatApiClient();

  const quantAnalysisId = quantAnalysis?.id;
  const quantVersionId = quantVersion?.id;
  const projectId = projectDetails.query.data?.project?.id;

  const query = useQuery({
    queryKey: queryKeys.projects.quantAnalysis.directoryTree({ projectId, quantAnalysisId, versionId: quantVersionId }),
    queryFn: () => {
      return apiClient.quantAnalysis.getQuantDirectoryTree({
        quantAnalysisId: quantAnalysisId,
        projectId: projectId,
        versionId: quantVersionId,
      });
    },
    enabled: !!(quantAnalysisId && projectId && quantVersionId),
  });

  // Use our custom search filter hook
  const { filteredOptions, folderResults } = useSearchFilter({
    searchValue,
    options,
    folderTreeData: query.data?.tree,
    isSearchable: true,
  });

  // Handle option selection
  const handleOptionSelect = useCallback((option: QuantAnalysisItem) => {
    // setSearchValue(option.name); // Commenting this out
    setSelectedItemId(option.id);
  }, [setSelectedItemId]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchValue('');
    }
  }, []);

  const handleNoOptionsMessage = useMemo(() => {
    return searchValue?.length ? `No analysis tabs match your search` : null;
  }, [searchValue]);

  return (
    <div className={styles.searchContainer}>
      <div className={styles.searchWrapper}>
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder="Search analyses..."
          isClearable={true} />
      </div>

      <div className={styles.dropdownContainer}>
        <SearchDropdown
          isOpen={isDropdownOpen}
          filteredOptions={filteredOptions}
          folderResults={folderResults}
          searchValue={searchValue}
          noOptionsMessage={handleNoOptionsMessage}
          onOptionSelect={handleOptionSelect}
          maxMenuHeight={windowSize.height - 250}
          minMenuHeight={windowSize.height - 250} />
      </div>
    </div>
  );
});

QuantAnalysisSearch.displayName = 'QuantAnalysisSearch';
